import React, { useEffect, useRef, useState } from "react";
import { API_BASE, api } from "@/lib/api";
import AppShell from "@/components/layout/AppShell";
import { useI18n } from "@/contexts/I18nContext";
import { Send, Sparkles, Plus, Trash2, MessageSquare } from "lucide-react";

const SUGGESTIONS = [
  { en: "Give me 5 content ideas for my Instagram this week", ar: "أعطني ٥ أفكار محتوى لإنستغرام هذا الأسبوع" },
  { en: "Write 3 captions for my new product launch", ar: "اكتب ٣ تعليقات لإطلاق منتجي الجديد" },
  { en: "Suggest 20 hashtags for fitness content", ar: "اقترح ٢٠ هاشتاغ لمحتوى اللياقة" },
  { en: "How can I improve my engagement rate?", ar: "كيف يمكنني تحسين معدل التفاعل؟" },
];

function renderInline(text) {
  // simple markdown: **bold**, *italic*, `code`, line breaks
  let out = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  out = out.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded-sm bg-muted text-xs">$1</code>');
  out = out.replace(/\n/g, "<br/>");
  return out;
}

export default function AIAssistantPage() {
  const { t, lang } = useI18n();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    const r = await api.get("/ai/chat/sessions");
    setSessions(r.data.sessions || []);
  };
  useEffect(() => { loadSessions(); }, []);

  const openSession = async (sid) => {
    setCurrentSession(sid);
    const r = await api.get(`/ai/chat/sessions/${sid}/messages`);
    setMessages(r.data.messages || []);
  };

  const newSession = () => { setCurrentSession(null); setMessages([]); };

  const deleteSession = async (sid) => {
    await api.delete(`/ai/chat/sessions/${sid}`);
    if (currentSession === sid) newSession();
    loadSessions();
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    const token = localStorage.getItem("nx_token");
    try {
      const resp = await fetch(`${API_BASE}/ai/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: msg, session_id: currentSession }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(p.slice(6));
            if (payload.session_id && !currentSession) setCurrentSession(payload.session_id);
            if (payload.delta) {
              assistantText += payload.delta;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: assistantText };
                return copy;
              });
            }
            if (payload.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: `⚠️ ${payload.error}` };
                return copy;
              });
            }
          } catch {}
        }
      }
      loadSessions();
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: `⚠️ ${e.message}` };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <AppShell>
      <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-8rem)]">
        {/* Session list */}
        <aside className="lg:w-64 shrink-0 nx-card p-3 flex flex-col" data-testid="ai-sessions-panel">
          <button data-testid="ai-new-session-btn" onClick={newSession} className="nx-btn-primary flex items-center justify-center gap-2 mb-3">
            <Plus className="w-4 h-4" /> {lang === "ar" ? "محادثة جديدة" : "New chat"}
          </button>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 px-2">{lang === "ar" ? "السجل" : "History"}</div>
          <div className="overflow-y-auto flex-1 space-y-1">
            {sessions.length === 0 && <div className="text-xs text-muted-foreground p-2">{lang === "ar" ? "لا محادثات بعد" : "No conversations yet"}</div>}
            {sessions.map((s) => (
              <div key={s.session_id} className={`group flex items-center gap-2 px-2 py-2 rounded-sm cursor-pointer text-sm ${currentSession === s.session_id ? "bg-primary/10 text-primary" : "hover:bg-muted"}`}>
                <MessageSquare className="w-3 h-3 shrink-0" />
                <button data-testid={`ai-session-${s.session_id}`} onClick={() => openSession(s.session_id)} className="flex-1 text-start truncate">{s.title || "Untitled"}</button>
                <button data-testid={`ai-session-delete-${s.session_id}`} onClick={() => deleteSession(s.session_id)} className="opacity-0 group-hover:opacity-100 transition">
                  <Trash2 className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* Chat area */}
        <div className="flex-1 nx-card flex flex-col min-w-0" data-testid="ai-chat-panel">
          <div className="border-b border-border/40 px-5 py-3 flex items-center gap-2">
            <div className="w-7 h-7 rounded-sm bg-primary/10 text-primary flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
            <div>
              <div className="font-medium text-sm">{lang === "ar" ? "🤖 مساعد دعم الذكي" : "🤖 DAAM AI"}</div>
              <div className="text-xs text-muted-foreground">{lang === "ar" ? "خبير استراتيجيات التسويق الرقمي" : "Digital marketing strategist"}</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.length === 0 && (
              <div className="max-w-2xl mx-auto text-center py-12" data-testid="ai-welcome">
                <div className="inline-block w-14 h-14 rounded-sm bg-primary/10 text-primary flex items-center justify-center mb-6"><Sparkles className="w-7 h-7" /></div>
                <h2 className="font-display text-2xl mb-2">{lang === "ar" ? "ماذا نُنمّي اليوم؟" : "What shall we grow today?"}</h2>
                <p className="text-sm text-muted-foreground mb-8">{lang === "ar" ? "اطلب أفكاراً، تعليقات، هاشتاغات، أو استراتيجية أسبوعية." : "Ask for content ideas, captions, hashtags, or a full weekly strategy."}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} data-testid={`ai-suggest-${i}`} onClick={() => send(s[lang])}
                      className="text-start p-3 border border-border rounded-sm hover:bg-muted/60 text-sm transition">
                      {s[lang]}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`ai-msg-${i}`} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-8 h-8 rounded-sm shrink-0 flex items-center justify-center text-xs font-semibold ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {m.role === "user" ? "You" : "AI"}
                </div>
                <div className={`max-w-[85%] px-4 py-3 rounded-sm text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted/40 border border-border"}`}
                  dangerouslySetInnerHTML={{ __html: renderInline(m.content || (streaming && i === messages.length - 1 ? "…" : "")) }}
                />
              </div>
            ))}
          </div>

          <div className="border-t border-border/40 p-3">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} data-testid="ai-form" className="flex gap-2">
              <input
                data-testid="ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={lang === "ar" ? "اكتب رسالتك..." : "Ask anything about your growth..."}
                className="flex-1 bg-background border border-border rounded-sm px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                disabled={streaming}
              />
              <button data-testid="ai-send-btn" type="submit" disabled={streaming || !input.trim()} className="nx-btn-primary px-4">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
